import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { connect } from 'react-redux';

// Import Routes
import { userRoutes, authRoutes } from './routes/allRoutes';

// Middleware and Layouts
import Authmiddleware from './routes/middleware/Authmiddleware';
import NonAuthLayout from './components/NonAuthLayout';

// SCSS & Toast
import './assets/scss/theme.scss';
import { Toaster } from 'react-hot-toast';

// Error Page
import Pages404 from 'pages/Utility/pages-404';
import Cookies from 'js-cookie';

// Function to check if a route is dynamic
const isDynamicRoute = (path) => /:.+/.test(path);

// Main App Component
const App = () => {
  const navigate = useNavigate();

  // Permission checker
  const hasPermission = (path) => {
    const userPermissions = localStorage.getItem('permissions');

    if (!userPermissions) {
      const allCookies = Cookies.get();
      for (const cookieName in allCookies) {
        Cookies.remove(cookieName);
      }
      navigate('/login');
      return false;
    }

    const permissions = JSON.parse(userPermissions);
    const pageUrls = new Set(permissions.map(p => p.pageUrl));
    const searchParams = new URLSearchParams(window.location.search);

    const permissionMap = {
      '/createCatalogue': { add: 'Add Catalogues', edit: 'Edit Catalogues' },
      '/createCustomer': { add: 'Add Customers', edit: 'Edit Customers' },
      '/createRole': { add: 'Add Roles', edit: 'Edit Roles' },
    };

    if (permissionMap[path]) {
      const isEdit = searchParams.has('id');
      const requiredPermission = isEdit ? permissionMap[path].edit : permissionMap[path].add;
      return permissions.some(p => p.permissionName === requiredPermission);
    }

    const relatedPaths = {
      '/workorders': '/createWorkOrder',
      '/sitesurveys': '/createSiteSurvey',
      '/catalogues': '/createCatalogue',
      '/customers': '/createCustomer',
      '/roles': '/createRole',
    };

    if (relatedPaths[path] && pageUrls.has(relatedPaths[path])) {
      return true;
    }

    return pageUrls.has(path);
  };

  // ProtectedRoute component
  const ProtectedRoute = ({ path, element }) => {
    const [allowed, setAllowed] = useState(null);
    const checkedRef = useRef(false);

    useEffect(() => {
      if (!checkedRef.current) {
        checkedRef.current = true;

        const isAllowed = isDynamicRoute(path) || hasPermission(path);
        if (isAllowed) {
          setAllowed(true);
        } else {
          navigate('/pages-403');
        }
      }
    }, [path, navigate]);

    if (allowed) return element;
    return null;
  };

  return (
    <React.Fragment>
      <Toaster />
      <Routes>
        {/* Auth Routes */}
        <Route>
          {authRoutes.map((route, idx) => (
            <Route
              path={route.path}
              element={<NonAuthLayout>{route.component}</NonAuthLayout>}
              key={idx}
              exact={true}
            />
          ))}
        </Route>

        {/* Default Redirect */}
        <Route path="/" element={<Navigate to="/dashboard" />} />

        {/* Protected User Routes */}
        <Route>
          {userRoutes.map((route, idx) => (
            <Route
              path={route.path}
              element={
                <Authmiddleware>
                  <ProtectedRoute path={route.path} element={route.component} />
                </Authmiddleware>
              }
              key={idx}
              exact={true}
            />
          ))}
        </Route>

        {/* Fallback 404 */}
        <Route path="*" element={<Pages404 />} />
      </Routes>
    </React.Fragment>
  );
};

App.propTypes = {
  layout: PropTypes.any,
};

const mapStateToProps = (state) => ({
  layout: state.Layout,
});

export default connect(mapStateToProps, null)(App);
